import { useState, useEffect } from 'react'
import { extractLinksFromClipboard, buildLinkBlock, mergeLinks } from '../lib/extractLinks'

function detectType(value) {
  const trimmed = value.trim()
  if (trimmed.includes('@') || trimmed.includes('.com') || trimmed.includes('.net') || trimmed.includes('.org')) return 'email'
  if (/[\d\s()+-]{7,}/.test(trimmed)) return 'phone'
  return 'email'
}

export default function ScanForm({ onScan, loading, initialType, initialValue }) {
  const [targetValue, setTargetValue] = useState(initialValue || '')
  const [userContext, setUserContext] = useState('')
  const [foundLinks, setFoundLinks] = useState([])
  const [pastedWithoutLinks, setPastedWithoutLinks] = useState(false)
  const [slowScan, setSlowScan] = useState(false)

  useEffect(() => {
    if (initialValue) setTargetValue(initialValue)
  }, [initialValue])

  useEffect(() => {
    if (loading) {
      const t = setTimeout(() => setSlowScan(true), 8000)
      return () => { clearTimeout(t); setSlowScan(false) }
    } else {
      setSlowScan(false)
    }
  }, [loading])

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!targetValue.trim()) {
      alert('Please enter an email address or phone number')
      return
    }
    const targetType = detectType(targetValue)
    const linkBlock = buildLinkBlock(foundLinks)
    const combinedContext = linkBlock ? `${userContext}\n\n${linkBlock}` : userContext
    onScan(targetType, targetValue, combinedContext)
  }

  // Harvest hrefs from the clipboard's HTML flavor. The textarea itself only ever
  // receives plain text, so without this the destinations are lost.
  const handleContextPaste = (e) => {
    const html = e.clipboardData?.getData('text/html') || ''
    const plain = e.clipboardData?.getData('text/plain') || ''
    const found = extractLinksFromClipboard(html, plain)
    if (found.length > 0) {
      setFoundLinks((prev) => mergeLinks(prev, found))
      setPastedWithoutLinks(false)
    } else {
      setPastedWithoutLinks(true)
    }
    // Not preventing default — the plain text still lands in the textarea as normal.
  }

  const removeLink = (href) => {
    setFoundLinks((prev) => prev.filter((l) => l.href !== href))
  }

  return (
    <div className="card">
      <h2 className="text-2xl font-bold mb-6 text-white">Scan for Fraud</h2>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Target Value */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Email Address or Phone Number
          </label>
          <input
            type="text"
            value={targetValue}
            onChange={(e) => setTargetValue(e.target.value)}
            placeholder="recruiter@company.com or +1234567890"
            className="input"
            disabled={loading}
          />
        </div>

        {/* User Context (Optional) */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Additional Context (Optional)
          </label>
          <textarea
            value={userContext}
            onChange={(e) => setUserContext(e.target.value)}
            onPaste={handleContextPaste}
            placeholder="Paste the message content here for better analysis..."
            rows={4}
            className="textarea"
            disabled={loading}
          />
          <p className="mt-2 text-xs text-gray-500">
            Paste the whole message. Any links are read automatically &mdash; you never
            need to click or copy them yourself.
          </p>
        </div>

        {/* Auto-extracted links. Rendered as plain text, never as anchors, so a
            malicious destination cannot be clicked from inside ScamView. */}
        {foundLinks.length > 0 && (
          <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/5 p-4">
            <div className="flex items-center gap-2 mb-3">
              <svg className="w-4 h-4 text-cyan-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 015.656 5.656l-3 3a4 4 0 01-5.656-5.656M10.172 13.828a4 4 0 01-5.656-5.656l3-3a4 4 0 015.656 5.656" />
              </svg>
              <span className="text-sm font-medium text-cyan-300">
                {foundLinks.length} link{foundLinks.length === 1 ? '' : 's'} found and will be checked
              </span>
            </div>
            <ul className="space-y-2">
              {foundLinks.map((link) => (
                <li key={link.href} className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    {link.text && (
                      <p className="text-xs text-gray-400 truncate">
                        shown as &ldquo;{link.text}&rdquo;
                      </p>
                    )}
                    {/* Deliberately not a link element */}
                    <p className="text-xs font-mono text-gray-300 break-all select-all">
                      {link.href}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeLink(link.href)}
                    disabled={loading}
                    className="text-xs text-gray-500 hover:text-gray-300 flex-shrink-0 disabled:opacity-50"
                    aria-label={`Remove ${link.href}`}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {pastedWithoutLinks && foundLinks.length === 0 && (
          <p className="text-xs text-gray-500">
            No links were found in what you pasted. If the message had a button or link,
            try copying it again directly from your email &mdash; forwarding or
            plain-text views can strip the destination.
          </p>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (slowScan ? 'Still scanning — this can take up to 60s on first run…' : 'Scanning...') : 'Scan Now'}
        </button>
      </form>
    </div>
  )
}
